import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  inject,
  HostListener,
  ViewChild,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuModule, Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { PermissionService } from '@app/core/services/permission.service';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import {
  StatusFilterComponent,
  ActionButton,
} from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';

import {
  LOGIC_TRANSFER_CONFIG,
  LOGIC_TRANSFER_TABLE_COLUMNS,
  LogicTransferItem,
  getLogicTransferActions,
  LogicTransferAction,
  formatLogicTransferStatus,
  LOGIC_TRANSFER_DEFAULT_COLUMNS_ORDER,
  LOGIC_TRANSFER_FLEXIBLE_COLUMNS_ORDER,
  LOGIC_TRANSFER_ACTION_COLUMN,
} from './logic-transfer.config';
import { LogicTransferService } from './logic-transfer.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { WarehouseControllerService } from '@app/api/stock-service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { from, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import {
  formatDateTimeWithLocale,
  formatDateWithLocale,
} from '@app/shared/utils/date-formatter.util';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';

@Component({
  selector: 'app-logic-transfer',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MenuModule,
    ConfirmDialogModule,
    TranslateModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  providers: [MessageService, ConfirmationService, LogicTransferService],
  templateUrl: './logic-transfer.component.html',
  styleUrls: ['./logic-transfer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogicTransferComponent implements OnInit {
  // Search configuration
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  searchConfig = signal(
    withPaginatedSearchFields(LOGIC_TRANSFER_CONFIG, {
      supportWarehouseIdList: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            status: true,
            typeCodes: ['MAIN', 'STORE'],
            valueKey: 'id',
          }),
      },
      receiveWarehouseIdList: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            status: true,
            typeCodes: ['MAIN', 'STORE'],
            valueKey: 'id',
          }),
      },
    }),
  );

  // Button configuration - computed based on permission
  primaryButton = computed((): ActionButton => ({
    label: 'common.actions.new',
    severity: 'info',
    show: this.canCreate(),
    icon: 'add',
  }));

  // Permission flags - computed from PermissionService
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  // Using specific submenu 'warehouse_transfer' for inventory permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.warehouse_transfer.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.warehouse_transfer.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.warehouse_transfer.edit');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.warehouse_transfer.approve');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.warehouse_transfer.delete');
  });

  // Check if user has any action permissions (READ, CREATE, UPDATE, APPROVE, or DELETE)
  hasAnyActionPermission = computed(
    () => this.canRead() || this.canCreate() || this.canUpdate() || this.canApprove() || this.canDelete()
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      LOGIC_TRANSFER_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of LOGIC_TRANSFER_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(LOGIC_TRANSFER_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      LOGIC_TRANSFER_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of LOGIC_TRANSFER_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  // Data management
  logicTransferItems = signal<LogicTransferItem[]>([]);
  filteredLogicTransferItems = signal<LogicTransferItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Warehouse mapping (ID -> {code, name})
  private warehouseMap = new Map<number, { code: string; name: string }>();

  // Pagination
  searchParams = signal({
    page: 1, // Use 1-based pagination
    size: 20,
  });

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<1 | -1 | undefined>(undefined);

  // Current search criteria (for maintaining filters when sorting/paging)
  private currentSearchCriteria: any = {};

  // Verify mode flag
  isVerify = signal(false);

  // Dialog signals
  selectedTransferOrder = signal<LogicTransferItem | null>(null);
  showApproveDialog = signal(false);
  showDeleteDialog = signal(false);
  showCancelDialog = signal(false);
  showApproveErrorDialog = signal(false);
  approveErrorMessage = signal<string>('');

  // Others panel state
  private othersPanelState = new Map<string, boolean>();

  // Current menu record for PrimeNG menu tracking
  currentMenuRecord = signal<LogicTransferItem | null>(null);
  currentMenuModel = signal<any[]>([]);

  // Flag to prevent double API calls
  private isSearching = false;

  // ViewChild references
  @ViewChild('actionMenu') actionMenu!: Menu;
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // Dialog button configurations
  approveDialogButtons: ConfirmationButton[] = [
    {
      label: 'logic_transfer.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'logic_transfer.dialog.actions.approve',
      action: 'confirm',
      severity: 'secondary',
    },
  ];

  deleteDialogButtons: ConfirmationButton[] = [
    {
      label: 'logic_transfer.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'logic_transfer.dialog.actions.delete',
      action: 'confirm',
      severity: 'danger',
    },
  ];

  cancelDialogButtons: ConfirmationButton[] = [
    {
      label: 'logic_transfer.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'common.actions.confirm',
      action: 'confirm',
      severity: 'danger',
    },
  ];

  approveErrorDialogButtons: ConfirmationButton[] = [
    {
      label: 'common.actions.close',
      action: 'close',
      severity: 'danger',
    },
  ];

  private router = inject(Router);
  private translateService = inject(TranslateService);
  private logicTransferService = inject(LogicTransferService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private errorTranslatorService = inject(ErrorTranslatorService);

  ngOnInit(): void {
    this.loadLogicTransferData();
    this.loadWarehouses();
    this.loadOptions();
  }

  // Get actions for a specific record
  getLogicTransferActions(
    record: LogicTransferItem,
    isVerify: boolean
  ): LogicTransferAction[] {
    return getLogicTransferActions(record, isVerify);
  }

  // Get menu items for PrimeNG menu
  getMenuItems(record: LogicTransferItem): any[] {
    const actions = this.getLogicTransferActions(record, this.isVerify());
    // Filter actions based on permissions
    const filteredActions = actions.filter((action) => {
      if (action.action === 'view') return this.canRead();
      if (action.action === 'edit') return this.canUpdate();
      if (action.action === 'approve') return this.canApprove();
      if (action.action === 'cancel') return this.canDelete();
      if (action.action === 'delete') return this.canDelete();
      if (action.action === 'createODO') return this.canCreate();
      return true; // Keep other actions as is
    });
    return filteredActions.map((action) => ({
      label: this.translateService.instant(action.label),
      command: () => this.onActionClick(action.action, record),
      disabled: action.disabled,
      visible: action.visible !== false,
    }));
  }

  // Toggle actions menu
  toggleActionsMenu(event: any, record: LogicTransferItem, menu: any): void {
    // Show the menu at the button position
    if (menu) {
      menu.toggle(event);
    }
  }

  // Format status for display
  formatLogicTransferStatus(status: string): string {
    return this.translateService.instant(formatLogicTransferStatus(status));
  }

  // Format creation time (with time)
  formatCreationTime(dateString: string | undefined): string {
    if (!dateString) return '-';
    return formatDateTimeWithLocale(dateString, 'vi-VN') || '-';
  }

  // Format expected receive date (date only)
  formatExpectedReceiveDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    return formatDateWithLocale(dateString, 'vi-VN') || '-';
  }

  // Load warehouses from API and cache in service
  private loadWarehouses(): void {
    this.logicTransferService.loadAllWarehouses().subscribe({
      next: (warehouses) => {
        // Build warehouse map for ID -> name/code lookup
        warehouses.forEach((item: any) => {
          if (item.id) {
            this.warehouseMap.set(item.id, {
              code: item.code || '',
              name: item.name || item.code || '',
            });
          }
        });

        // Remap currently loaded items (if any) now that warehouseMap is ready
        const currentItems = this.logicTransferItems();
        if (currentItems && currentItems.length > 0) {
          const remapped = currentItems.map((item) =>
            this.mapWarehousesToNames(item)
          );
          this.logicTransferItems.set(remapped);
          this.filteredLogicTransferItems.set(remapped);
        }
      },
      error: (error) => {
        // Error handled silently
      },
    });
  }

  // Load options with translations
  private loadOptions(): void {
    // Translate status options
    const statusField = this.searchConfig().find(
      (field) => field.key === 'status'
    );
    if (statusField && statusField.options) {
      const translatedOptions = statusField.options.map((option) => ({
        ...option,
        label: this.translateService.instant(option.label),
      }));

      this.searchConfig.set(
        this.searchConfig().map((field) =>
          field.key === 'status'
            ? { ...field, options: translatedOptions }
            : field
        )
      );
    }

    // Translate warehouse options (warehouses are already loaded from API)
    const sourceWarehouseField = this.searchConfig().find(
      (field) => field.key === 'supportWarehouseIdList'
    );
    if (sourceWarehouseField && sourceWarehouseField.options) {
      // Options are already loaded from API, just keep them as is
      // No need to translate since they come from API
    }

    const destinationWarehouseField = this.searchConfig().find(
      (field) => field.key === 'receiveWarehouseIdList'
    );
    if (destinationWarehouseField && destinationWarehouseField.options) {
      // Options are already loaded from API, just keep them as is
      // No need to translate since they come from API
    }
  }

  // Handle Add New button click
  onAddNew(): void {
    // Check permission before navigating
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.router.navigate(['/stock/transfer-order/add']);
  }

  // Handle action button clicks
  onActionClick(action: string, record?: LogicTransferItem): void {
    const targetRecord = record || this.currentMenuRecord();

    if (!targetRecord) {
      return;
    }

    // Close the panel if it's open
    const recordId = targetRecord.id || targetRecord.code || 'unknown';
    if (action !== 'view') {
      this.othersPanelState.set(recordId, false);
      this.currentMenuRecord.set(null);
    }

    // Execute action immediately
    switch (action) {
      case 'view':
        this.onViewDetails(targetRecord);
        break;
      case 'edit':
        this.onEdit(targetRecord);
        break;
      case 'approve':
        this.onApprove(targetRecord);
        break;
      case 'delete':
        this.onDelete(targetRecord);
        break;
      case 'cancel':
        this.onCancel(targetRecord);
        break;
      case 'createODO':
        this.onCreateODO(targetRecord);
        break;
      default:
        // Unknown action, do nothing
        break;
    }
  }

  // Action methods
  private onEdit(record: LogicTransferItem): void {
    // Check permission before navigating
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    const recordId = record.id || record.code || '';
    if (!recordId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid record ID',
      });
      return;
    }
    this.router.navigate(['/stock/transfer-order/edit', recordId]);
  }

  private onExecute(record: LogicTransferItem): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to execute this logic transfer?',
      header: 'Confirm Execution',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.executeLogicTransfer(record.code);
      },
    });
  }

  private onExecuteAgain(record: LogicTransferItem): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to execute this logic transfer again?',
      header: 'Confirm Re-execution',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.executeLogicTransfer(record.code);
      },
    });
  }

  private onResetTransfer(record: LogicTransferItem): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to reset this logic transfer?',
      header: 'Confirm Reset',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.resetLogicTransfer(record.code);
      },
    });
  }

  private onInvalidate(record: LogicTransferItem): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to invalidate this logic transfer?',
      header: 'Confirm Invalidation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.invalidateLogicTransfer(record.code);
      },
    });
  }

  private onApprove(record: LogicTransferItem): void {
    // Check permission before action
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.selectedTransferOrder.set(record);
    this.showApproveDialog.set(true);
  }

  onApproveConfirm(action: string): void {
    if (action === 'confirm') {
      // Check permission before confirming
      if (!this.canApprove()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.permissions.no_permission'),
          detail: this.translateService.instant('common.permissions.no_permission'),
        });
        this.showApproveDialog.set(false);
        this.selectedTransferOrder.set(null);
        return;
      }
      const transferOrder = this.selectedTransferOrder();
      if (transferOrder) {
        this.approveLogicTransfer(transferOrder.id || transferOrder.code || '');
      }
    }
    this.showApproveDialog.set(false);
    this.selectedTransferOrder.set(null);
  }

  private onDelete(record: LogicTransferItem): void {
    // Check permission before action
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.selectedTransferOrder.set(record);
    this.showDeleteDialog.set(true);
  }

  onDeleteConfirm(action: string): void {
    if (action === 'confirm') {
      // Check permission before confirming
      if (!this.canDelete()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.permissions.no_permission'),
          detail: this.translateService.instant('common.permissions.no_permission'),
        });
        this.showDeleteDialog.set(false);
        this.selectedTransferOrder.set(null);
        return;
      }
      const transferOrder = this.selectedTransferOrder();
      if (transferOrder) {
        this.deleteLogicTransfer(transferOrder.code);
      }
    }
    this.showDeleteDialog.set(false);
    this.selectedTransferOrder.set(null);
  }

  private onCreateODO(record: LogicTransferItem): void {
    // Check permission before navigating
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    // Navigate to create outbound delivery order page with transfer order code
    this.router.navigate(['/stock/delivery/delivery-order/add'], {
      queryParams: { transferOrderCode: record.code },
    });
  }

  // API calls for actions
  private executeLogicTransfer(id: string): void {
    this.logicTransferService.executeLogicTransfer(id).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: response.message,
        });
        this.loadLogicTransferData();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to execute logic transfer',
        });
      },
    });
  }

  private onCancel(record: LogicTransferItem): void {
    // Check permission before action - cancel requires canDelete permission
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.selectedTransferOrder.set(record);
    this.showCancelDialog.set(true);
  }

  onCancelConfirm(action: string): void {
    if (action === 'confirm') {
      // Check permission before confirming - cancel requires canDelete permission
      if (!this.canDelete()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.permissions.no_permission'),
          detail: this.translateService.instant('common.permissions.no_permission'),
        });
        this.showCancelDialog.set(false);
        this.selectedTransferOrder.set(null);
        return;
      }
      const transferOrder = this.selectedTransferOrder();
      if (transferOrder) {
        this.cancelLogicTransfer(transferOrder.id || transferOrder.code || '');
      }
    }
    this.showCancelDialog.set(false);
    this.selectedTransferOrder.set(null);
  }

  private cancelLogicTransfer(id: string): void {
    this.logicTransferService.cancelLogicTransfer(id).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail:
            response.message ||
            this.translateService.instant(
              'logic_transfer.messages.cancelled_successfully'
            ),
        });
        this.loadLogicTransferData();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'logic_transfer.messages.failed_to_cancel'
          ),
        });
      },
    });
  }

  private resetLogicTransfer(id: string): void {
    this.logicTransferService.resetLogicTransfer(id).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: response.message,
        });
        this.loadLogicTransferData();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to reset logic transfer',
        });
      },
    });
  }

  private invalidateLogicTransfer(id: string): void {
    this.logicTransferService.invalidateLogicTransfer(id).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: response.message,
        });
        this.loadLogicTransferData();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to invalidate logic transfer',
        });
      },
    });
  }

  private approveLogicTransfer(id: string): void {
    this.logicTransferService.approveLogicTransfer(id).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail:
            response.message ||
            this.translateService.instant(
              'logic_transfer.messages.approved_successfully'
            ),
        });
        this.loadLogicTransferData();
      },
      error: (error) => {
        // Extract error message from API response using utility function
        const errorMessage = extractErrorMessage(error);

        // Parse and translate error message if we have one
        if (errorMessage) {
          // Use ErrorTranslatorService to translate error message (includes pattern matching)
          // Pass errorMessage as fallback so if no pattern matches, it returns original message
          const translatedMessage = this.errorTranslatorService.translate(
            errorMessage,
            errorMessage // Use original message as fallback if no pattern matches
          );
          this.approveErrorMessage.set(translatedMessage);
        } else {
          // Fallback to default message if no error message from API
          this.approveErrorMessage.set(
            this.translateService.instant(
              'logic_transfer.messages.failed_to_approve'
            )
          );
        }

        // Show error dialog
        this.showApproveErrorDialog.set(true);
      },
    });
  }

  onApproveErrorClose(action: string): void {
    this.showApproveErrorDialog.set(false);
    this.approveErrorMessage.set('');
  }

  private deleteLogicTransfer(id: string): void {
    this.logicTransferService.deleteLogicTransfer(id).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail:
            response.message ||
            this.translateService.instant(
              'logic_transfer.messages.deleted_successfully'
            ),
        });
        this.loadLogicTransferData();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'logic_transfer.messages.failed_to_delete'
          ),
        });
      },
    });
  }

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }

  private loadLogicTransferData(): void {
    this.loading.set(true);

    const searchCriteria = this.currentSearchCriteria || {};
    const sortBy = this.sortField() || 'creationDate';
    const ascending = this.sortOrder() === 1;

    this.logicTransferService
      .getLogicTransfers(this.searchParams().page, this.searchParams().size, {
        ...searchCriteria,
        sortBy,
        ascending,
      })
      .subscribe({
        next: (response: any) => {
          // Map warehouse IDs to code/name for display
          const enrichedData = response.data.map((item: LogicTransferItem) =>
            this.mapWarehousesToNames(item)
          );

          this.logicTransferItems.set(enrichedData);
          this.filteredLogicTransferItems.set(enrichedData);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);

          // Show success message if data loaded successfully
          if (enrichedData.length > 0) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Loaded ${enrichedData.length} logic transfer records`,
            });
          }
        },
        error: (error: any) => {
          // Error loading data
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load logic transfer data. Please try again.',
          });
          this.loading.set(false);

          // Set empty data on error
          this.logicTransferItems.set([]);
          this.filteredLogicTransferItems.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onSearch(searchCriteria: any): void {
    if (this.isSearching) return; // Prevent double calls

    this.isSearching = true;
    this.loading.set(true);

    // Clean up search criteria - remove empty values
    let cleanCriteria = this.cleanSearchCriteria(searchCriteria);

    // Map warehouse filters (array hoặc đơn)
    if (cleanCriteria.supportWarehouseIdList !== undefined) {
      const src = cleanCriteria.supportWarehouseIdList;
      const srcId = Array.isArray(src) ? src[0] : src;
      if (srcId !== null && srcId !== undefined && srcId !== '') {
        cleanCriteria = { ...cleanCriteria, supportWarehouseId: srcId };
      }
      delete cleanCriteria.supportWarehouseIdList;
    }

    if (cleanCriteria.receiveWarehouseIdList !== undefined) {
      const dest = cleanCriteria.receiveWarehouseIdList;
      const destId = Array.isArray(dest) ? dest[0] : dest;
      if (destId !== null && destId !== undefined && destId !== '') {
        cleanCriteria = { ...cleanCriteria, receiveWarehouseId: destId };
      }
      delete cleanCriteria.receiveWarehouseIdList;
    }

    // Convert status from string (UI) to array (API expects array)
    if (cleanCriteria.status && typeof cleanCriteria.status === 'string') {
      cleanCriteria = { ...cleanCriteria, status: [cleanCriteria.status] };
    }

    // Save search criteria for later use (e.g., when sorting)
    this.currentSearchCriteria = cleanCriteria;

    const sortBy = this.sortField() || 'creationDate';
    const ascending = this.sortOrder() === 1;

    this.logicTransferService
      .getLogicTransfers(
        1, // Reset to first page (1-based for service)
        this.searchParams().size,
        {
          ...cleanCriteria,
          sortBy,
          ascending,
        }
      )
      .subscribe({
        next: (response: any) => {
          const enrichedData = response.data.map((item: LogicTransferItem) =>
            this.mapWarehousesToNames(item)
          );

          this.filteredLogicTransferItems.set(enrichedData);
          this.totalRecords.set(response.totalRecords);
          this.searchParams.set({ ...this.searchParams(), page: 1 });
          this.loading.set(false);
          this.isSearching = false;

          // Close all panels
          this.othersPanelState.clear();
          this.currentMenuRecord.set(null);

          // Show search results message
          if (response.totalRecords > 0) {
            this.messageService.add({
              severity: 'info',
              summary: 'Search Results',
              detail: `Found ${response.totalRecords} items matching your criteria`,
            });
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'No Results',
              detail: 'No items found matching your search criteria',
            });
          }
        },
        error: (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to search logic transfer data. Please try again.',
          });
          this.loading.set(false);
          this.isSearching = false;

          // Set empty data on error
          this.filteredLogicTransferItems.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  /** Map warehouse IDs to code/name for display */
  private mapWarehousesToNames(item: LogicTransferItem): LogicTransferItem {
    const enriched = { ...item };

    const supportId =
      typeof item.supportLogicWarehouseId === 'string'
        ? parseInt(item.supportLogicWarehouseId, 10)
        : item.supportLogicWarehouseId;
    const receiveId =
      typeof item.receivingLogicWarehouseId === 'string'
        ? parseInt(item.receivingLogicWarehouseId, 10)
        : item.receivingLogicWarehouseId;

    const supportWh = supportId ? this.warehouseMap.get(supportId) : undefined;
    const receiveWh = receiveId ? this.warehouseMap.get(receiveId) : undefined;

    // Map transfer type to text
    const transferTypeMap: Record<string, string> = {
      MAIN_TO_STORE: this.translateService.instant(
        'logic_transfer.form.transfer_type.main_to_store'
      ),
      STORE_TO_STORE: this.translateService.instant(
        'logic_transfer.form.transfer_type.store_to_store'
      ),
    };
    enriched.transferTypeName =
      transferTypeMap[item.transferType] || item.transferType || '';

    if (supportWh) {
      enriched.supportWarehouseCode = supportWh.code;
      enriched.supportWarehouseName = supportWh.name;
    }
    if (receiveWh) {
      enriched.receiveWarehouseCode = receiveWh.code;
      enriched.receiveWarehouseName = receiveWh.name;
    }

    return enriched;
  }

  onReset(): void {
    this.searchParams.set({ ...this.searchParams(), page: 1 });
    this.currentSearchCriteria = {}; // Clear search criteria
    this.sortField.set(undefined); // Reset sort field
    this.sortOrder.set(undefined); // Reset sort order
    this.loadLogicTransferData();

    // Close all panels
    this.othersPanelState.clear();
    this.currentMenuRecord.set(null);

    this.messageService.add({
      severity: 'info',
      summary: 'Reset',
      detail: 'Search criteria has been reset',
    });
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );

    // Close all panels
    this.othersPanelState.clear();
    this.currentMenuRecord.set(null);

    this.loadLogicTransferData();
  }

  onSortChange(event: { field: string; order: 1 | -1 }): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order);
    this.searchParams.set({ ...this.searchParams(), page: 1 }); // Reset to first page when sorting
    this.loadLogicTransferData();
  }

  onViewDetails(record: LogicTransferItem): void {
    // Check permission before navigating
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    const recordId = record.id || record.code || 'unknown';
    this.router.navigate(['/stock/transfer-order/view'], {
      queryParams: { id: recordId },
    });
  }

  // Clean up search criteria by removing empty/null values
  private cleanSearchCriteria(criteria: any): any {
    const cleanCriteria: any = {};

    Object.keys(criteria).forEach((key) => {
      const value = criteria[key];

      // Keep non-empty values
      if (value !== null && value !== undefined && value !== '') {
        // For arrays, keep if they have items
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleanCriteria[key] = value;
          }
        } else {
          cleanCriteria[key] = value;
        }
      }
    });

    return cleanCriteria;
  }

  // Refresh data
  refreshData(): void {
    this.logicTransferService.refreshData();
    this.messageService.add({
      severity: 'info',
      summary: 'Refresh',
      detail: 'Data refreshed successfully',
    });
  }

  // Toggle Others panel for a specific record
  toggleOthersPanel(record: LogicTransferItem): void {
    const recordId = record.id || record.code || 'unknown';
    const currentState = this.othersPanelState.get(recordId) || false;

    // Close all other panels first
    this.othersPanelState.clear();

    // Then open this panel if it wasn't already open
    if (!currentState) {
      this.othersPanelState.set(recordId, true);
    }
  }

  // Toggle menu for Others actions
  toggleMenu(event: Event, record: LogicTransferItem): void {
    event.preventDefault();
    event.stopPropagation();

    // Set the current record and menu model
    this.currentMenuRecord.set(record);
    this.currentMenuModel.set(this.getActionsModel(record));

    // Toggle the menu using ViewChild reference
    if (this.actionMenu) {
      this.actionMenu.toggle(event);
    }
  }

  // Get actions model for PrimeNG menu
  getActionsModel(record: LogicTransferItem): any[] {
    const actions = this.getLogicTransferActions(record, this.isVerify());

    // Filter actions based on permissions
    const filteredActions = actions.filter((action) => {
      if (action.action === 'view') return false; // View is handled separately
      if (action.action === 'edit') return this.canUpdate();
      if (action.action === 'approve') return this.canApprove();
      if (action.action === 'cancel') return this.canDelete();
      if (action.action === 'delete') return this.canDelete();
      if (action.action === 'createODO') return this.canCreate();
      return action.visible !== false; // Keep other actions if visible
    });

    const menuItems = filteredActions.map((action) => ({
      label: this.translateService.instant(action.label),
      command: () => {
        this.onActionClick(action.action);
      },
      disabled: action.disabled,
      visible: action.visible !== false,
    }));

    return menuItems;
  }

  // Check if there are any actions (excluding view) after permission filtering
  hasOtherActions(record: LogicTransferItem): boolean {
    const actions = this.getLogicTransferActions(record, this.isVerify());
    
    // Filter actions based on permissions (excluding view)
    const filteredActions = actions.filter((action) => {
      if (action.action === 'view') return false; // View is handled separately
      if (action.action === 'edit') return this.canUpdate();
      if (action.action === 'approve') return this.canApprove();
      if (action.action === 'cancel') return this.canDelete();
      if (action.action === 'delete') return this.canDelete();
      if (action.action === 'createODO') return this.canCreate();
      return action.visible !== false; // Keep other actions if visible
    });

    return filteredActions.length > 0;
  }

  // Close all panels when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Check if click is outside the actions column
    const target = event.target as HTMLElement;
    if (!target.closest('.inline-flex')) {
      // Close all panels
      this.othersPanelState.clear();
      this.currentMenuRecord.set(null);
    }
  }

  // Handle keyboard events
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      // Close all panels on Esc key
      this.othersPanelState.clear();
      this.currentMenuRecord.set(null);
    }
  }
}
